// X221 (R90 L-3): our 250-country list keeps some catalogue spellings ("Man (Isle of)", "Virgin Islands (US)",
// "Gambia The", "Hong Kong S.A.R."). The data stays as it is (exports and other clients read it); every web picker and
// sentence shows the natural name below, and the picker search also finds a country by its other common names
// ("Isle of Man", "UK", "Ivory Coast"), accents ignored. Pure, so the tests and every caller agree.

/** ISO → the name people use, and other names they type. A country not listed keeps its own name. */
export const COUNTRY_DISPLAY = Object.freeze({
  AX: {name: 'Åland Islands', aliases: ['Aland Islands']},
  AG: {name: 'Antigua and Barbuda'},
  BS: {name: 'Bahamas', aliases: ['The Bahamas']},
  BQ: {name: 'Caribbean Netherlands', aliases: ['Bonaire', 'Sint Eustatius', 'Saba']},
  CV: {name: 'Cape Verde', aliases: ['Cabo Verde']},
  CI: {name: "Côte d'Ivoire", aliases: ['Ivory Coast']},
  CZ: {name: 'Czech Republic', aliases: ['Czechia']},
  CD: {name: 'Democratic Republic of the Congo', aliases: ['DR Congo', 'DRC', 'Congo-Kinshasa']},
  CG: {name: 'Republic of the Congo', aliases: ['Congo', 'Congo-Brazzaville']},
  TL: {name: 'East Timor', aliases: ['Timor-Leste']},
  SZ: {name: 'Eswatini', aliases: ['Swaziland']},
  FJ: {name: 'Fiji', aliases: ['Fiji Islands']},
  GM: {name: 'Gambia', aliases: ['The Gambia']},
  GG: {name: 'Guernsey', aliases: ['Alderney', 'Sark', 'Guernsey and Alderney', 'Channel Islands']},
  JE: {name: 'Jersey', aliases: ['Channel Islands']},
  HK: {name: 'Hong Kong'},
  IM: {name: 'Isle of Man', aliases: ['Man']},
  KR: {name: 'South Korea', aliases: ['Korea', 'Republic of Korea']},
  KP: {name: 'North Korea', aliases: ['Korea', 'DPRK']},
  MO: {name: 'Macau', aliases: ['Macao']},
  MK: {name: 'North Macedonia', aliases: ['Macedonia']},
  MD: {name: 'Moldova', aliases: ['Republic of Moldova']},
  MM: {name: 'Myanmar', aliases: ['Burma']},
  NL: {name: 'Netherlands', aliases: ['Holland', 'The Netherlands']},
  PS: {name: 'Palestine', aliases: ['Palestinian Territories']},
  PG: {name: 'Papua New Guinea'},
  PN: {name: 'Pitcairn Islands', aliases: ['Pitcairn Island']},
  RE: {name: 'Réunion', aliases: ['Reunion']},
  BL: {name: 'Saint Barthélemy', aliases: ['St Barts', 'Saint-Barthelemy']},
  SH: {name: 'Saint Helena', aliases: ['St Helena']},
  KN: {name: 'Saint Kitts and Nevis', aliases: ['St Kitts']},
  LC: {name: 'Saint Lucia', aliases: ['St Lucia']},
  MF: {name: 'Saint Martin (French part)', aliases: ['St Martin', 'Saint-Martin']},
  PM: {name: 'Saint Pierre and Miquelon'},
  VC: {name: 'Saint Vincent and the Grenadines', aliases: ['St Vincent']},
  SX: {name: 'Sint Maarten', aliases: ['Saint Martin (Dutch part)', 'St Maarten']},
  GS: {name: 'South Georgia and the South Sandwich Islands', aliases: ['South Georgia']},
  SJ: {name: 'Svalbard and Jan Mayen'},
  TT: {name: 'Trinidad and Tobago'},
  TR: {name: 'Turkey', aliases: ['Türkiye']},
  TC: {name: 'Turks and Caicos Islands'},
  AE: {name: 'United Arab Emirates', aliases: ['UAE']},
  GB: {name: 'United Kingdom', aliases: ['UK', 'Great Britain', 'Britain', 'England', 'Scotland', 'Wales', 'Northern Ireland']},
  US: {name: 'United States', aliases: ['USA', 'United States of America', 'America']},
  VA: {name: 'Vatican City', aliases: ['Holy See']},
  VG: {name: 'British Virgin Islands', aliases: ['Virgin Islands (British)', 'Virgin Islands']},
  VI: {name: 'US Virgin Islands', aliases: ['Virgin Islands (US)', 'United States Virgin Islands', 'Virgin Islands']},
  WF: {name: 'Wallis and Futuna'},
});

const isoOf = (c) => (c?.isoCode ?? c?.iso ?? c?.countryCode ?? '').toString().toUpperCase();

/** The name to show for a country ({name, isoCode}); its own name when we have no better one. */
export function countryDisplayName(country) {
  if (!country) return '';
  return COUNTRY_DISPLAY[isoOf(country)]?.name ?? country.name ?? '';
}

/** A country object with its display name (the catalogue spelling kept as `catalogueName`). */
export function withDisplayName(country) {
  if (!country) return country;
  const name = countryDisplayName(country);
  return name === country.name ? country : {...country, name, catalogueName: country.name};
}

/** Every name a country can be found by: the shown name, the catalogue spelling, the aliases. */
export function countrySearchNames(country) {
  if (!country) return [];
  const d = COUNTRY_DISPLAY[isoOf(country)];
  return [countryDisplayName(country), country.catalogueName, country.name, ...(d?.aliases ?? [])].filter(Boolean);
}

const fold = (s) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

/** Whether a typed search finds this country (any of its names, accents and case ignored). */
export function countryMatches(country, query) {
  const q = fold(query);
  if (!q) return true;
  return countrySearchNames(country).some((n) => fold(n).includes(q));
}

/**
 * Vuetify custom-filter for a country v-autocomplete: the title's own match (so it is highlighted), else any other
 * name of the country (shown without a highlight), else no match.
 */
export function countryFilter(value, query, item) {
  const title = fold(value);
  const q = fold(query);
  if (!q) return 0;
  const at = title.indexOf(q);
  if (at >= 0) return [at, at + q.length];
  return countryMatches(item?.raw ?? item, query) ? true : -1;
}

// ── "in the United Kingdom": the countries whose name takes "the" in a sentence ──
const TAKES_THE = new Set(['GB', 'US', 'NL', 'PH', 'CZ', 'AE', 'BS', 'GM', 'MV', 'KM', 'SC', 'CD', 'CG', 'CF', 'DO', 'UM', 'IM', 'MP', 'MH', 'SB', 'KY', 'FK', 'FO', 'CK', 'CC', 'TC', 'VG', 'VI', 'AX', 'PN', 'GS', 'BQ']);

/** Whether the country's name takes "the" ("in the United Kingdom", "in the Netherlands", "in France"). */
export function countryTakesThe(country) {
  const iso = isoOf(country);
  if (iso) return TAKES_THE.has(iso);
  const n = String(country?.name ?? '');
  return /^(United |Republic |Democratic )|Islands$/.test(n) || ['Netherlands', 'Philippines', 'Czech Republic', 'Bahamas', 'Gambia', 'Maldives'].includes(n);
}

/** The country as a sentence says it: "the United Kingdom", "Spain". A plain name string is taken as it is. */
export function countryInSentence(country) {
  const c = typeof country === 'string' ? {name: country} : country;
  const name = countryDisplayName(c);
  if (!name) return '';
  return countryTakesThe(c) && !/^the /i.test(name) ? `the ${name}` : name;
}
